import { Disclosure, Transition } from '@headlessui/react';
import dayjs, { Dayjs } from 'dayjs';
import omit from 'lodash/omit';
import { Dispatch, SetStateAction, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { ArchiveSwitch } from '@/modules/common/components/ArchiveSwitch.tsx';
import { IssuerDashboardWrapperOutletContext } from '@/modules/issuer-dashboard/components/IssuerDashboardWrapper.tsx';
import { transfersFilters } from '@/modules/issuer-dashboard/components/holder-tabs/TransfersTab.tsx';
import { useIssuerAddActivityMenuOptions } from '@/modules/issuer-dashboard/hooks/useIssuerAddActivityMenuOptions.ts';
import { Button, CustomCheckbox, FilterItem, RoundBadge, Spinner, getSelectOptionByEnum } from 'ui';
import { FiltersIcon } from 'ui/icons';
import { fixNameEnum } from 'ui/utils';
import { ShareholderExtendResponse, SortedBy, TransactionStatus } from 'api';

const statusOptions = [
  { label: fixNameEnum(TransactionStatus.Completed), value: TransactionStatus.Completed },
  { label: fixNameEnum(TransactionStatus.Pending), value: TransactionStatus.Pending },
  { label: fixNameEnum(TransactionStatus.Unsuccessfull), value: TransactionStatus.Unsuccessfull },
];
const sortOptions = getSelectOptionByEnum<SortedBy>(SortedBy);

interface HolderTransfersTabFiltersProps {
  filters: transfersFilters;
  setFilters: Dispatch<SetStateAction<transfersFilters>>;
  shareholder: ShareholderExtendResponse | null;
  isDeleted?: boolean;
  setIsDeleted?: Dispatch<SetStateAction<boolean | undefined>>;
  isFetching?: boolean;
}

const HolderTransfersTabFilters = ({
  filters,
  setFilters,
  shareholder,
  isDeleted,
  setIsDeleted,
  isFetching = false,
}: HolderTransfersTabFiltersProps) => {
  const { status, sorted_by, from_requested_at_date, to_requested_at_date } = filters;
  const { registerId } = useOutletContext<IssuerDashboardWrapperOutletContext>();
  const addActivityOptions = useIssuerAddActivityMenuOptions({
    registerId: Number(registerId),
    shareholderId: shareholder?.id,
  });

  const confirmationDateValue =
    from_requested_at_date && to_requested_at_date
      ? ([dayjs(from_requested_at_date), dayjs(to_requested_at_date)] as [Dayjs, Dayjs])
      : undefined;

  const appliedFiltersLength = Object.keys(filters).length;

  const handleToggleActionRequiredFirst = useCallback(
    (checked: boolean) => {
      setFilters(filters =>
        checked ? { ...filters, sorted_by: SortedBy.Status } : omit(filters, 'sorted_by'),
      );
    },
    [setFilters],
  );

  const handleChangeStatus = useCallback(
    (status: TransactionStatus | null) => {
      setFilters(filters => (status ? { ...filters, status } : omit(filters, 'status')));
    },
    [setFilters],
  );

  const handleChangeConfirmationDate = useCallback(
    (date: [Dayjs, Dayjs] | null) => {
      setFilters(filters => {
        return date
          ? {
              ...filters,
              from_requested_at_date: date[0].toISOString(),
              to_requested_at_date: date[1].endOf('d').toISOString(),
            }
          : omit(filters, ['from_requested_at_date', 'to_requested_at_date']);
      });
    },
    [setFilters],
  );

  const handleChangeSortedBy = useCallback(
    (sorted_by: SortedBy | null) => {
      setFilters(filters => (sorted_by ? { ...filters, sorted_by } : omit(filters, 'sorted_by')));
    },
    [setFilters],
  );

  return (
    <Disclosure>
      <div className="flex items-center justify-between flex-wrap gap-4">
        <CustomCheckbox
          label="Show Action required first"
          checked={sorted_by === SortedBy.Status}
          onChange={e => handleToggleActionRequiredFirst(e.target.checked)}
        />
        <div className="flex items-center gap-4 flex-wrap">
          {setIsDeleted && (
            <>
              {isFetching && <Spinner size="sm" className="m-0 mt-1" />}
              <ArchiveSwitch
                active={!!isDeleted}
                onChange={active => setIsDeleted(active || undefined)}
              />
            </>
          )}
          <Button
            className="w-[140px]"
            onClick={addActivityOptions.find(item => item.label === 'Transfer')?.onClick}
          >
            Add transfer
          </Button>
          <Disclosure.Button>
            <div className="flex items-center border px-4 py-[0.5rem] rounded border-[rgba(23, 23, 23, 0.12)] gap-2">
              <img alt="filters" src={FiltersIcon} />
              <span className="text-sm">Filters</span>
              {!!appliedFiltersLength && <RoundBadge content={appliedFiltersLength} />}
            </div>
          </Disclosure.Button>
        </div>
      </div>

      <Transition
        enter="transition duration-100 ease-out"
        enterFrom="transform scale-95 opacity-0"
        enterTo="transform scale-100 opacity-100"
        leave="transition duration-75 ease-out"
        leaveFrom="transform scale-100 opacity-100"
        leaveTo="transform scale-95 opacity-0"
      >
        <Disclosure.Panel static>
          <div className="flex flex-wrap gap-4 justify-end">
            <FilterItem
              label="Date range"
              type="date"
              classes={{ label: 'min-w-[85px]', element: 'min-w-[200px]' }}
              onChange={handleChangeConfirmationDate}
              value={confirmationDateValue}
            />
            <FilterItem
              label="Status"
              type="select"
              classes={{ label: 'min-w-[85px]', element: 'min-w-[200px]' }}
              value={status}
              options={statusOptions}
              onChange={handleChangeStatus}
            />
            <FilterItem
              label="Sorting"
              type="select"
              classes={{ label: 'min-w-[85px]', element: 'min-w-[200px]' }}
              value={sorted_by}
              options={sortOptions}
              onChange={handleChangeSortedBy}
            />
          </div>
        </Disclosure.Panel>
      </Transition>
    </Disclosure>
  );
};

export default HolderTransfersTabFilters;
